// @flow
import * as React from "react";
import styled, { css } from "styled-components";
import moment from "moment";
import Gettext from "../common/Gettext";
import { MoneyBlock, MoneyValue } from "./Money";
import {
  Cup as CupIcon,
  Wallet as WalletIcon,
  Rating as RatingIcon,
  Person as PersonIcon,
  Coins as CoinIcon,
  Timer as TimerIcon
} from "../Icons/Tournaments";
import Money from "../Money";
import HowMuchInBucks from "../HowMuchInBucks";
import * as S from "../Tournaments/Popups/styles";
import Kuchka, { tournamentCurrency } from "../Icons/Kuchka";
import { getDuration, IncompletedTime } from "../Tournaments/Timer";

export const List = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  padding-top: 23px;
  padding-bottom: 26px;
  border-top: 1px solid #95989a;

  @media (max-width: 959px) {
    flex-direction: column;
    justify-content: flex-start;
  }
`;

const ItemWrapper = styled.div`
  direction: ${props => props.theme.direction};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-right: ${({ theme }) => (theme.direction === "rtl" ? "0" : "10px")};
  margin-left: ${({ theme }) => (theme.direction === "rtl" ? "10px" : "0")};
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  text-align: ${({ theme }) => (theme.direction === "rtl" ? "right" : "left")};

  @media (max-width: 959px) {
    margin-bottom: 20px;

    :last-child {
      margin-bottom: 0;
    }

    ${props => {
      if (props.horizontal) {
        return css`
          flex-direction: row;
          align-items: center;
          margin-bottom: 10px;
        `;
      }

      return css`
        margin-left: 10px;
      `;
    }};
  }
`;

const Title = styled.h4`
  direction: ${props => props.theme.direction};
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  color: #99959d;
  text-transform: capitalize;

  ${props => {
    if (props.horizontal) {
      return css`
        min-width: 110px;
        margin-right: 5px;
      `;
    }

    return css`
      margin-bottom: 5px;
    `;
  }};
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  color: #99959d;
`;

const Strong = styled.strong`
  font-weight: 700;
`;

const ExtraStrong = styled(Strong)`
  color: ${({ theme }) => theme.foreground};
`;

const IconWrapper = styled.div`
  margin-right: ${({ theme }) => (theme.direction === "rtl" ? 0 : "6px")};
  margin-left: ${({ theme }) => (theme.direction === "rtl" ? "6px" : 0)};
  color: ${({ theme }) => theme.foreground};
`;

const MoneyContainer = styled.div`
  display: flex;
  color: #99959d;
`;

const Free = styled.div`
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  color: ${({ theme }) => theme.foreground};
`;

export const ItemPrizeFund = props => (
  <ItemWrapper horizontal={props.horizontal}>
    {props.finished ? (
      <Title horizontal={props.horizontal}>
        <Gettext>Your Winnings</Gettext>:
      </Title>
    ) : (
      <Title horizontal={props.horizontal}>
        <Gettext>Prize fund</Gettext>:
      </Title>
    )}
    <Content>
      <IconWrapper>
        <CupIcon width="16px" />
      </IconWrapper>
      {props.finished ? (
        <MoneyContainer>
          <HowMuchInBucks
            value={props.value}
            valueUSD={props.valueUSD}
            divider="&nbsp;/&nbsp;"
          />
        </MoneyContainer>
      ) : (
        <MoneyContainer>
          <HowMuchInBucks
            currency={props.currency}
            value={props.value}
            valueUSD={props.valueUSD}
            valueStyle={S.Value}
            symbolStyle={S.Currency}
            divider="&nbsp;/&nbsp;"
          />
        </MoneyContainer>
      )}
    </Content>
  </ItemWrapper>
);

export const ItemResult = props => (
  <ItemWrapper horizontal={props.horizontal}>
    <Title horizontal={props.horizontal}>
      <Gettext>Your results</Gettext>:
    </Title>
    <Content>
      <IconWrapper>
        <CupIcon width="16px" />
      </IconWrapper>
      <MoneyContainer>
        <Strong>
          <HowMuchInBucks value={props.value} valueUSD={props.valueUSD} />
        </Strong>
      </MoneyContainer>
    </Content>
  </ItemWrapper>
);

export const ItemCost = props => (
  <ItemWrapper horizontal={props.horizontal}>
    <Title horizontal={props.horizontal}>
      <Gettext>Participation fee</Gettext>:
    </Title>
    <Content>
      <IconWrapper>
        <CoinIcon width="26px" />
      </IconWrapper>
      <MoneyContainer>
        {(!props.is_limited && props.type === "free") || (props.is_limited && props.invited) ? (
          <Free>
            <Gettext>Free</Gettext>
          </Free>
        ) : (
          <HowMuchInBucks
            value={props.participation_price}
            valueUSD={props.participation_price_usd}
            valueStyle={S.Value}
            symbolStyle={S.Currency}
            divider="&nbsp;/&nbsp;"
          />
        )}
      </MoneyContainer>
    </Content>
  </ItemWrapper>
);

export const ItemParticipants = props => (
  <ItemWrapper horizontal={props.horizontal}>
    <Title horizontal={props.horizontal}>
      <Gettext>Participants</Gettext>:
    </Title>
    <Content>
      <IconWrapper>
        <PersonIcon width="16px" />
      </IconWrapper>
      <MoneyBlock size="14px">
        <MoneyValue>{props.count || props.participant_count}</MoneyValue>
      </MoneyBlock>
    </Content>
  </ItemWrapper>
);

export const ItemRate = props => (
  <ItemWrapper horizontal={props.horizontal}>
    <Title horizontal={props.horizontal}>
      <Gettext>Your rating</Gettext>:
    </Title>
    <Content>
      <IconWrapper>
        <RatingIcon width="18px" />
      </IconWrapper>
      <span>
        # <ExtraStrong>{props.current_place}</ExtraStrong> <Gettext>of</Gettext>{" "}
        <ExtraStrong>{props.participant_count}</ExtraStrong>
      </span>
    </Content>
  </ItemWrapper>
);

export const ItemBalance = props => {
  const isParticipate =
    props.participation_status === 1 && props.account != null;

  return (
    <ItemWrapper horizontal={props.horizontal}>
      {isParticipate ? (
        <Title horizontal={props.horizontal}>
          <Gettext>Tournament balance</Gettext>:
        </Title>
      ) : (
        <Title horizontal={props.horizontal}>
          <Gettext>Starting balance</Gettext>:
        </Title>
      )}
      <Content>
        <IconWrapper>
          <WalletIcon width="18px" />
        </IconWrapper>

        {isParticipate ? (
          <Money currency={tournamentCurrency} Symbol={Kuchka}>
            {props.account.balance}
          </Money>
        ) : (
          <Money currency={tournamentCurrency} Symbol={Kuchka}>
            {props.account_money}
          </Money>
        )}
      </Content>
    </ItemWrapper>
  );
};

export const Duration = props => (
  <ItemWrapper horizontal={props.horizontal}>
    <Title horizontal={props.horizontal}>
      <Gettext>Duration</Gettext>:
    </Title>
    <Content>
      <IconWrapper>
        <TimerIcon width="16px" />
      </IconWrapper>
      <ExtraStrong>
        {moment
          .duration(
            moment(props.finish_date * 1000).diff(
              moment(props.start_date * 1000)
            )
          )
          .humanize()}
      </ExtraStrong>
    </Content>
  </ItemWrapper>
);

export const DurationTime = props => (
  <ItemWrapper horizontal={props.horizontal}>
    <Title horizontal={props.horizontal}>
      <Gettext>Duration</Gettext>:
    </Title>
    <Content>
      <IconWrapper>
        <TimerIcon width="16px" />
      </IconWrapper>
      <IncompletedTime
        {...props}
        days={getDuration(props, "days", "toStart")}
        hours={getDuration(props, "hours", "toStart")}
        minutes={getDuration(props, "minutes", "toStart")}
        seconds={getDuration(props, "seconds", "toStart")}
        active={false}
        isCompleted={false}
        labelOnTop={false}
      />
    </Content>
  </ItemWrapper>
);



// WEBPACK FOOTER //
// ./src/components/Tournament/PersonalInfo.js